import type {
	ActionContract,
	AnswerValue,
	ChoiceDef,
	Condition,
	FlowCtx,
	FlowDef,
	NodeDef,
	QuestionNode,
	TransitionInput,
} from './flowSchema';
import { ACTION_CONTRACTS } from './actionContracts';

/**
 * La ÚNICA autoridad de navegación. Todo consumidor (runtime, transición,
 * resume, boot redirect, mapa dev, tests) deriva de estas funciones puras;
 * no existe otra forma de decidir "a dónde va el usuario".
 *
 * Contrato de resolución de edges (una sola forma, sin escalera):
 * - `next` string → edge incondicional.
 * - `next` mapa → se indexa por el valor de la respuesta (question single/
 *   binary) o por el outcome (action); la llave 'default' es el fallback.
 * - Un nodo invisible (visibleWhen falso) se atraviesa resolviendo su next
 *   como si fuera incondicional; validateFlow garantiza que eso es resoluble.
 */

/** Una llave de ctx AUSENTE vale `false`, no "indefinido que no iguala a nada".
 * El ctx es Partial por diseño (cada dueño arma el suyo: host, sandbox, tests),
 * y sin este default un `eq: false` —la forma de "muéstrame esto salvo que
 * pase X"— dejaba el nodo INVISIBLE en todo ctx que no declarara la llave. Es
 * decir: agregar un flag nuevo apagaba pasos del spine en los dueños que aún no
 * lo conocían, en silencio. Ausente = la condición no se cumple = el nodo se ve. */
const ctxValue = (ctx: FlowCtx, key: keyof FlowCtx): boolean | string =>
	ctx[key] ?? false;

export const evalCondition = (
	condition: Condition,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): boolean => {
	if ('and' in condition) return condition.and.every((c) => evalCondition(c, answers, ctx));
	if ('or' in condition) return condition.or.some((c) => evalCondition(c, answers, ctx));
	if ('ctx' in condition) return ctxValue(ctx, condition.ctx) === condition.eq;

	const value = answers[condition.answer];
	if ('eq' in condition) return value === condition.eq;
	return value !== condition.neq;
};

export const isVisible = (
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): boolean =>
	node.kind === 'terminal' || !node.visibleWhen
		? true
		: evalCondition(node.visibleWhen, answers, ctx);

/** Is the choice offered under this ctx? One without `visibleWhen` always is.
 * The reducer (legal answers), the runner (what the screen paints) and the
 * enumerator read it from here. */
export const isChoiceVisible = (choice: ChoiceDef, ctx: FlowCtx): boolean =>
	!choice.visibleWhen || ctxValue(ctx, choice.visibleWhen.ctx) === choice.visibleWhen.eq;

/** The question as the user sees it: choices hidden by their `visibleWhen` are
 * dropped. Returns the same node when nothing is hidden. */
export const withVisibleChoices = (node: QuestionNode, ctx: FlowCtx): QuestionNode => {
	const choices = node.choices;
	if (!choices) return node;
	const offered = Object.entries(choices).filter(([, choice]) => isChoiceVisible(choice, ctx));
	return offered.length === Object.keys(choices).length
		? node
		: { ...node, choices: Object.fromEntries(offered) };
};

/** Llaves de answer que una condición referencia (recursivo sobre and/or).
 * Lo usa el enumerador generativo (modelWalk) para proyectar qué respuestas
 * son ruta. */
export const conditionAnswerKeys = (condition: Condition): string[] => {
	if ('and' in condition) return condition.and.flatMap(conditionAnswerKeys);
	if ('or' in condition) return condition.or.flatMap(conditionAnswerKeys);
	if ('ctx' in condition) return [];
	return [condition.answer];
};

/** Llaves de ctx que una condición referencia (espejo de conditionAnswerKeys). */
export const conditionCtxKeys = (condition: Condition): string[] => {
	if ('and' in condition) return condition.and.flatMap(conditionCtxKeys);
	if ('or' in condition) return condition.or.flatMap(conditionCtxKeys);
	if ('ctx' in condition) return [condition.ctx];
	return [];
};

/**
 * ¿Este nodo es de los que una campaña de aliado contesta POR el usuario?
 *
 * No hace falta una lista: el flow ya lo declara. Un nodo cuya visibilidad
 * depende de `campaignPrefilled` es, por definición, uno que el link de campaña
 * ya respondió — hoy `goals`, `ally_grid` y `discovery_source`.
 *
 * Se deriva y no se enumera para que un nodo nuevo con esa condición quede
 * cubierto solo: una lista a mano envejece en silencio, y lo que cuelga de esto
 * es que el «atrás» no devuelva al usuario de una campaña pagada al grid de los
 * otros aliados (ver `campaignLocked` en flowSchema).
 */
export const isCampaignOwned = (node: NodeDef): boolean => {
	if (node.kind === 'terminal' || !node.visibleWhen) return false;
	return conditionCtxKeys(node.visibleWhen).includes('campaignPrefilled');
};

/** Visibilidad OPTIMISTA para la vista de flujo: ¿existe alguna combinación de
 * respuestas aún no contestadas que muestre este nodo con el ctx actual? Un
 * nodo condicional a una respuesta futura (p.ej. la asesoría gateada por
 * situation) NO está oculto — está por decidirse; solo cuenta como oculto
 * cuando la condición es falsa con lo ya conocido (átomos de answer sin
 * responder evalúan optimistas; el ctx del panel siempre es conocido). */
export const possiblyVisible = (
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): boolean => {
	if (node.kind === 'terminal' || !node.visibleWhen) return true;
	const optimistic = (condition: Condition): boolean => {
		if ('and' in condition) return condition.and.every(optimistic);
		if ('or' in condition) return condition.or.some(optimistic);
		if ('ctx' in condition) return ctxValue(ctx, condition.ctx) === condition.eq;
		const value = answers[condition.answer];
		if (value === undefined) return true;
		return 'eq' in condition ? value === condition.eq : value !== condition.neq;
	};
	return optimistic(node.visibleWhen);
};

/** Llave bajo la que se persiste el outcome de un nodo action. */
export const outcomeKey = (nodeId: string): string => `${nodeId}.outcome`;

/** ¿El input es un OUTCOME que SALE del onboarding sin completar (contrato
 * `exits`, p.ej. explorar)? El dueño del estado (host y sandbox) lo intercepta
 * ANTES de aplicar la transición y lo ejecuta como PUERTA DE SALIDA (presencia
 * 'exited' + navegación a home): el `next` del nodo no cubre el outcome y su
 * valor jamás se persiste. Una sola fuente para ambos dueños — sin duplicar el
 * predicado. */
export const isExitOutcome = (
	flow: FlowDef,
	position: string | null,
	input: TransitionInput,
): boolean => {
	if (input.type !== 'outcome' || !position) return false;
	const node = flow.nodes[position];
	if (!node || node.kind !== 'action') return false;
	const exits = (ACTION_CONTRACTS as Record<string, ActionContract>)[node.action]?.exits;
	return !!exits && exits.includes(input.value);
};

/** ¿El nodo puede ser una posición del usuario? Sólo pregunta si es VISIBLE con
 * el ctx actual.
 *
 * Antes había una segunda condición: un action "irreversible" con outcome
 * grabado quedaba CONSUMIDO y dejaba de ser posición para siempre. Se quitó
 * (31-ago-2026, decisión de Rafael) porque escondía pantallas por un hecho del
 * pasado en vez de por el estado presente, y eso falla en la dirección peor:
 * bastaba un outcome mal reportado —un carrito abandonado que se contaba como
 * compra— para que el usuario ya no pudiera volver a esa pantalla NUNCA, y el
 * walk lo atravesara ruteando fuera del onboarding.
 *
 * Lo que decide si una pantalla se muestra es el ESTADO VIVO, o sea `ctx`:
 * `hasActivePlan` esconde la compra de plan, `isLinked` esconde la vinculación.
 * Un dato que se puede volver a consultar y que se corrige solo cuando la
 * realidad cambia, en vez de una marca grabada que nadie puede desmarcar.
 *
 * Lo que sí se conserva es el REPLAY: el fast-forward de re-entrada vuelve a
 * aplicar el outcome grabado y pasa de largo, así que quien de verdad compró no
 * se topa otra vez con la pantalla al recargar. Pasar de largo y estar
 * prohibido no son lo mismo — el back sigue alcanzando el nodo. */
const isLanding = (
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): boolean => isVisible(node, answers, ctx);

const edgeKeyFor = (
	nodeId: string,
	node: NodeDef,
	answers: Record<string, AnswerValue>,
): string | null => {
	if (node.kind === 'question') {
		const value = answers[node.saves];
		return typeof value === 'string' ? value : null;
	}
	if (node.kind === 'action') {
		const value = answers[outcomeKey(nodeId)];
		return typeof value === 'string' ? value : null;
	}
	return null;
};

/** Resuelve el edge de salida de un nodo dado el estado de answers, o null si
 * el nodo aún no tiene con qué decidir (frontera del walk). */
export const resolveNext = (
	nodeId: string,
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	explicitKey?: string,
): string | null => {
	if (node.kind === 'terminal') return null;
	if (typeof node.next === 'string') return node.next;
	const key = explicitKey ?? edgeKeyFor(nodeId, node, answers);
	if (key && node.next[key]) return node.next[key];
	return node.next['default'] ?? null;
};

const infoSeenKey = (nodeId: string): string => `seen.${nodeId}`;

/** Llave bajo la que se persiste el TARGET de un skip — el skip es data del
 * estado, no un movimiento efímero: el replay del walk lo respeta. Regresar
 * con back al nodo saltado lo BORRA (reconsiderar > persistir). */
export const skipKey = (nodeId: string): string => `skip.${nodeId}`;

// ── Enumeración ESTÁTICA de edges (única fuente: validateFlow, el reducer y
//    la vista de flujo derivan de aquí — jamás reimplementar este recorrido) ──

/** Targets del `next` sin resolver answers: string → [next]; mapa → values. */
export const staticNextTargets = (node: NodeDef): string[] => {
	if (node.kind === 'terminal') return [];
	return typeof node.next === 'string' ? [node.next] : Object.values(node.next);
};

/** Target estático del skip, o null si el skip no crea un edge ('none', o un
 * 'next' irresoluble — mapa sin 'default'). El reducer usa EXACTAMENTE esto:
 * el skip nunca depende de answers. */
export const staticSkipTarget = (node: NodeDef): string | null => {
	if (node.kind === 'terminal' || node.skip === 'none') return null;
	if (node.skip === 'next') {
		if (typeof node.next === 'string') return node.next;
		return typeof node.next['default'] === 'string' ? node.next['default'] : null;
	}
	return node.skip;
};

/** Todos los targets salientes estáticos (next + skip explícito), para grafo,
 * validación y layout. */
export const staticOutgoingTargets = (node: NodeDef): string[] => {
	const targets = staticNextTargets(node);
	const skipTarget = staticSkipTarget(node);
	if (skipTarget && node.kind !== 'terminal' && node.skip !== 'none' && node.skip !== 'next') {
		targets.push(skipTarget);
	}
	return targets;
};

/** Edge de ATRAVESADO de un nodo no-aterrizable. Un nodo sólo es no-aterrizable
 * si es INVISIBLE, y se atraviesa INCONDICIONAL (next string o 'default') —
 * NUNCA por la respuesta stale de una visita previa: tras abandonar un branch
 * por back, esa respuesta re-rutearía al usuario adentro del branch equivocado
 * (bug real: zubale_inscrito='yes' stale mandaba a un usuario Uber al perfil
 * Zubale). */
const passthroughNext = (
	node: NodeDef,
): string | null => {
	if (node.kind === 'terminal') return null;
	if (typeof node.next === 'string') return node.next;
	return node.next['default'] ?? null;
};

const isTraversed = (
	nodeId: string,
	node: NodeDef,
	answers: Record<string, AnswerValue>,
): boolean => {
	if (node.kind === 'question') return answers[node.saves] !== undefined;
	if (node.kind === 'action') return answers[outcomeKey(nodeId)] !== undefined;
	if (node.kind === 'info') return answers[infoSeenKey(nodeId)] === true;
	return false;
};

export type WalkResult = {
	/** Nodos visibles recorridos, en orden, incluida la frontera. */
	path: string[];
	/** Nodo donde el usuario está parado: primer visible sin responder, o el
	 * terminal si el flow quedó cubierto. */
	frontier: string;
	frontierIsTerminal: boolean;
};

/** Camina el flow replay-ando answers desde start. El resume ES esta función:
 * recargar reconstruye la FRONTERA (primer visible sin responder) desde los
 * datos — no la última posición viva: la posición tras un back es efímera y
 * no se persiste (ver el contrato de back en applyTransition). */
export const walk = (
	flow: FlowDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): WalkResult => {
	const path: string[] = [];
	const guard = Object.keys(flow.nodes).length + 1;
	let currentId: string | null = flow.start;

	for (let hops = 0; currentId && hops <= guard; hops++) {
		const node: NodeDef | undefined = flow.nodes[currentId];
		if (!node) break;

		if (!isLanding(node, answers, ctx)) {
			currentId = passthroughNext(node);
			continue;
		}

		path.push(currentId);
		if (node.kind === 'terminal') {
			return { path, frontier: currentId, frontierIsTerminal: true };
		}
		if (isTraversed(currentId, node, answers)) {
			currentId = resolveNext(currentId, node, answers);
			continue;
		}
		const skippedTo: AnswerValue | undefined = answers[skipKey(currentId)];
		if (typeof skippedTo === 'string') {
			currentId = skippedTo;
			continue;
		}
		return { path, frontier: currentId, frontierIsTerminal: false };
	}

	const frontier = path[path.length - 1] ?? flow.start;
	return { path, frontier, frontierIsTerminal: false };
};

/** Primer nodo ATERRIZABLE llegando a `startId` (él mismo si es visible y no
 * consumido; si no, atraviesa invisibles/consumidos resolviendo su next — un
 * consumido rutea por su outcome grabado). Los terminales siempre aterrizan.
 * Null solo si un edge es irresoluble (validateFlow lo impide). */
export const firstVisibleFrom = (
	flow: FlowDef,
	startId: string,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): string | null => {
	const guard = Object.keys(flow.nodes).length + 1;
	let currentId: string | null = startId;
	for (let hops = 0; currentId && hops <= guard; hops++) {
		const node: NodeDef | undefined = flow.nodes[currentId];
		if (!node) return null;
		if (isLanding(node, answers, ctx)) return currentId;
		currentId = passthroughNext(node);
	}
	return null;
};

/** Estima cuántos nodos visibles quedan por delante de `fromId` siguiendo un
 * edge representativo por nodo (el resoluble por answers, o el primero del
 * mapa) — el denominador de la barra de progreso del runner. Vive AQUÍ porque
 * es una travesía del grafo: el runner no recorre edges por su cuenta. */
export const remainingDepth = (
	flow: FlowDef,
	fromId: string,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): number => {
	const seen = new Set<string>();
	let current: string | null = fromId;
	let remaining = 0;
	while (current && !seen.has(current)) {
		seen.add(current);
		const node: NodeDef | undefined = flow.nodes[current];
		if (!node || node.kind === 'terminal') break;
		const next: string | Record<string, string> = node.next;
		let nextId: string | null;
		if (typeof next === 'string') {
			nextId = next;
		} else {
			nextId = resolveNext(current, node, answers) ?? Object.values(next)[0] ?? null;
		}
		if (!nextId) break;
		current = nextId;
		const nextNode: NodeDef | undefined = flow.nodes[current];
		if (nextNode && isVisible(nextNode, answers, ctx)) remaining += 1;
	}
	return remaining;
};

/** Siguiente nodo visible SALIENDO de `fromId` — un paso posicional. El avance
 * en vivo del reducer usa esto (avanzar = ir al nodo que sigue de donde el
 * usuario está parado, aunque haya respuestas más adelante por un back);
 * el resume usa walk() (replay desde start hasta la frontera). */
export const nextVisible = (
	flow: FlowDef,
	fromId: string,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): string | null => {
	const next = resolveNext(fromId, flow.nodes[fromId], answers);
	return next ? firstVisibleFrom(flow, next, answers, ctx) : null;
};

/** Valor inicial del input de un question text: la respuesta ya guardada gana
 * (revisita por back), si no, el prefill declarado leído de ctx (p.ej. el
 * nombre que ya vive en el user profile). */
export const initialTextValue = (
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): string => {
	if (node.kind !== 'question') return '';
	const answered = answers[node.saves];
	if (typeof answered === 'string') return answered;
	const prefilled = node.prefill ? ctx[node.prefill] : undefined;
	return typeof prefilled === 'string' ? prefilled : '';
};

/** Selección inicial de una pregunta 'multi': la respuesta guardada manda, y si
 * no hay, la pre-selección de ctx (`prefillChoices`) filtrada contra las choices
 * declaradas — un valor que el flow no ofrece se ignora en silencio en vez de
 * marcar un chip fantasma.
 *
 * NO la usa autoAdvanceInput: una pre-selección no contesta la pregunta, así que
 * la re-entrada sigue frenando en el nodo. */
export const initialChoiceValues = (
	node: NodeDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
): string[] => {
	if (node.kind !== 'question' || node.variant !== 'multi') return [];
	const answered = answers[node.saves];
	if (Array.isArray(answered)) return answered;
	const raw = node.prefillChoices ? ctx[node.prefillChoices] : undefined;
	if (typeof raw !== 'string' || raw === '') return [];
	const offered = new Set(Object.keys(withVisibleChoices(node, ctx).choices ?? {}));
	return [...new Set(raw.split(',').map((value) => value.trim()))].filter((value) =>
		offered.has(value),
	);
};

/** The question a node asks on its own screen (`companion`), without the
 * choices it omits (`companionOmits`), or null. */
export const companionOf = (flow: FlowDef, node: NodeDef): QuestionNode | null => {
	if (node.kind !== 'question' || node.companion === undefined) return null;
	const companion = flow.nodes[node.companion];
	if (companion?.kind !== 'question') return null;
	const omitted = new Set(node.companionOmits);
	if (omitted.size === 0) return companion;
	return {
		...companion,
		choices: Object.fromEntries(
			Object.entries(companion.choices ?? {}).filter(([key]) => !omitted.has(key)),
		),
	};
};

/** Nodo visible anterior a `from` en el path actual (para back). */
export const previousVisible = (
	flow: FlowDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
	from: string,
): string | null => {
	const { path } = walk(flow, answers, ctx);
	const index = path.indexOf(from);
	return index > 0 ? path[index - 1] : null;
};

export const INFO_SEEN_KEY = infoSeenKey;
